import { ReactNode } from 'react';

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
}

export function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none text-sm font-medium border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 bg-white text-slate-700 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 cursor-pointer transition-colors hover:border-slate-300"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-arrow-down-s-line text-slate-400 text-sm"></i>
        </div>
      </div>
    </div>
  );
}

interface FilterPillsProps {
  label?: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string; color?: string }[];
}

export function FilterPills({ label, value, onChange, options }: FilterPillsProps) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {label && <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{label}</span>}
      <div className="flex items-center gap-1 flex-wrap">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-pointer whitespace-nowrap ${
              value === opt.value
                ? opt.color || 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface FilterSearchProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  width?: string;
}

export function FilterSearch({ value, onChange, placeholder = 'Search...', width = 'sm:w-64' }: FilterSearchProps) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
        <i className="ri-search-line text-slate-400 text-sm"></i>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`pl-9 pr-8 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100 focus:bg-white transition-colors w-full ${width}`}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-close-line text-slate-400 text-sm hover:text-slate-600"></i>
        </button>
      )}
    </div>
  );
}

interface FilterToggleProps {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}

export function FilterToggle({ label, checked, onChange }: FilterToggleProps) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`w-9 h-5 rounded-full transition-colors cursor-pointer flex items-center px-0.5 flex-shrink-0 ${checked ? 'bg-emerald-600' : 'bg-slate-200'}`}
      >
        <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${checked ? 'translate-x-4' : 'translate-x-0'}`}></div>
      </div>
      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">{label}</span>
    </label>
  );
}

interface FilterBarProps {
  children: ReactNode;
  count?: number;
  countLabel?: string;
  className?: string;
}

export function FilterBar({ children, count, countLabel = 'results', className = '' }: FilterBarProps) {
  return (
    <div className={`bg-white border-b border-slate-100 sticky top-16 z-40 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {children}
          {count !== undefined && (
            <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">
              {count} {countLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
